import { css } from '@emotion/core';

import {
  success, gray3, gray5, info, gray1,
} from 'styles/global_defaults/colors';
import {
  standardSpacing, quarterSpacing, threeQuartersSpacing, halfSpacing,
} from 'styles/global_defaults/scaffolding';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import React, {
  useRef, useState, createRef, useEffect,
} from 'react';
import { PopoverContent, Overlay } from 'react-bootstrap';

import { isTouchDevice, handheld, notHandheld } from 'styles/global_defaults/media-queries';

export type BarChartUnitData = {
  label: string,
  total: number,
  active: number,
};

type BarChartProps = {
  data: BarChartUnitData[],
  /** Label for the 'total' value of each bar, in gray */
  totalLabel: string,
  /** Label for the 'active' value of each bar, in green */
  activeLabel: string,
  /** The name of the data been shown in the chart, displayed in the legend */
  dataName: string,
};

export const BarChart = (props: BarChartProps) => {
  // TODO: Make this responsive, or at least configurable
  const chartPixelHeight = 200;
  const maxBarHeight = 180;
  const activeColor = success;

  const legendDot = css`
    /* Draw colored boxes on children of the element this is applied to,
      to indicate the legend item's color */
    & > *::before {
      display: inline-block;
      width: 10px;
      height: 10px;
      border-radius: 90%;
      background: ${activeColor};
      margin-right: ${halfSpacing}px;
      content: ' ';
    }

    .legend-active::before {
      background: ${activeColor};
    }

    .legend-total::before {
      background: ${gray3};
    }
  `;

  const styles = css`
    display: flex;
    flex-direction: column;

    .title {
      margin-bottom: ${threeQuartersSpacing}px;
    }

    .chart {
      justify-content: center;
      height: auto;
      text-align: center;
      display: -ms-grid;
      display: grid;
      width: 100%;
      ${notHandheld(css`
        grid-template-columns: repeat(12, auto);
        /* See https://css-tricks.com/css-grid-in-ie-debunking-common-ie-grid-misconceptions/#article-header-id-1 for strange IE11 syntax */
        /* Adds two 'auto filling' columns on the left and right to correct the chart not centering */
        -ms-grid-columns: 1fr (auto)[12] 1fr;
      `)};
      ${handheld(css`
        grid-template-columns: repeat(6, auto);
        -ms-grid-columns: 1fr (auto)[6] 1fr;
        grid-row-gap: ${standardSpacing}px;
        grid-auto-rows: auto;
      `)};
      margin-left: auto;
      margin-right: auto;
    };

    .unit-data-container {
      display: flex;
      align-items: center;
      align-self: flex-end;
      flex-direction: column-reverse;
      padding-left: ${halfSpacing}px;
      padding-right: ${halfSpacing}px;
      padding-top: ${quarterSpacing}px;
      height: ${chartPixelHeight + quarterSpacing}px;

      /* Display the bar tooltips either on hover or when clicked */
      &:hover, &.clicked {
        background: ${info};
        cursor: pointer;
      }

      &.disabled .bar {
        background: ${gray1};
        opacity: .3;
      }

      &.disabled > .chart-label {
        color: ${gray1};
        opacity: .3;
      }
    }

    .bar-container {
      display: flex;
      align-items: flex-end;
    }

    .bar {
      display: inline-block;
      width: 5px;
      border-radius: 5px;
      &:not(:last-of-type) {
        margin-right: ${quarterSpacing}px;
      }
    }

    .total-bar {
      background: ${gray3};
    }

    .active-bar {
      background: ${activeColor};
    }

    .legend {
      display: flex;
      justify-content: center;
      margin-top: ${threeQuartersSpacing}px;
      & > *:not(:last-of-type) {
        padding-right: ${standardSpacing * 2}px;
      }

      ${legendDot};
    }

    .legend-total {
      color: ${gray3};
    }

    .chart-label {
      /* TODO: Remove, fix whatever is making our fonts fail to load */
      font-family: 'Open Sans Condensed';
      padding-top: ${quarterSpacing}px;
    }
  `;

  let maxValue = 1;
  props.data.forEach((dataUnit) => {
    maxValue = Math.max(maxValue, dataUnit.active, dataUnit.total);
  });

  const getBarHeight = (value: number): string => `${Math.max((value / maxValue) * maxBarHeight, 1)}px`;

  const refs = useRef([]);

  useEffect(() => {
    refs.current = refs.current.slice(0, props.data.length);
  }, [props.data]);

  // Tracks which of the bar charts have been clicked and toggles their display 'on' until outside the chart
  // is clicked
  const [clickedBar, setClickedBar]: [number, Function] = useState(null);
  const popoverStyle = css`
    .popover-legend {
      ${legendDot};

      & > *::before {
        margin-right: ${quarterSpacing}px; /* Overrides legendDot's margin */
      }
    }
  `;

  // TODO: Figure out a more sane way to use B4 styles instead of B3 without needing to wrap this in a react-app class
  const dataUnitPopover = (dataUnit: BarChartUnitData) => (popoverProps) => (
    <Popover css={popoverStyle} {...popoverProps} id='popover-basic' show={popoverProps.show.toString()}>
      <PopoverContent>
        <div className='popover-legend'>
          <div className='legend-item legend-total text-small'>{`${dataUnit.total} ${props.totalLabel}`}</div>
          <div className='legend-item legend-active text-small'>{`${dataUnit.active} ${props.activeLabel}`}</div>
        </div>
      </PopoverContent>
    </Popover>
  );

  // Listen for mouse down and leave events to display the bar chart popover on click and hide it on leave
  const getClickedBarClass = (i: number) => (clickedBar === i ? 'clicked' : '');
  const onBarClicked = (e: React.MouseEvent, i: number) => {
    if (!isTouchDevice()) return;
    setClickedBar(i);
    e.preventDefault(); // Prevent triggering the body click listener added below
  };
  const onBarEnter = (e: React.MouseEvent, i: number) => setClickedBar(i);
  const onChartLeave = (e: React.MouseEvent) => setClickedBar(null);

  /* Listen on the body for clicks so we can hide the popover if you click outside of the chart */
  useEffect(() => {
    const updateClickedBar = () => {
      if (isTouchDevice()) {
        setClickedBar(null);
      }
    };
    document.body.addEventListener('mousedown', updateClickedBar);
    return () => document.body.removeEventListener('mousedown', updateClickedBar);
  });

  // TODO: Remove this disable and give the data items an id
  /* eslint-disable react/no-array-index-key */
  return (
    <div css={styles}>
      <div className='title text-xl font-weight-bold condensed text-center'>
        {props.totalLabel}
        {' '}
        vs.
        {' '}
        {props.activeLabel}
      </div>

      <div className='chart' onMouseLeave={(e) => onChartLeave(e)}>
        {props.data.map((dataUnit, i) => (
          <React.Fragment key={`fragment-${i}`}>
            <Overlay
              target={refs.current[i]}
              key={`data-unit-${i}`}
              show={clickedBar === i}
              placement='top'
              transition={false}
            >
              {dataUnitPopover(dataUnit)}
            </Overlay>
            <div
              key={`chart-bar-${i}`}
              ref={el => { refs.current[i] = el; return el; }}
              className={`unit-data-container ${getClickedBarClass(i)} ${dataUnit.active === 0 && dataUnit.total === 0 ? 'disabled' : ''}`}
              /* Grid column indices are 1 based. We also offset by another '1' to account for the auto filling first column that was
              added to orrect the chart not auto centering
               */
              style={{ msGridColumn: `${i + 2}`, msGridRow: '1' } as any}
              onClick={(e) => onBarClicked(e, i)}
              onMouseEnter={(e) => { onBarEnter(e, i); }}
            >
              <div className='chart-label text-small font-weight-bold'>{dataUnit.label}</div>
              <div className='bar-container'>
                <div className='bar total-bar' css={{ height: getBarHeight(dataUnit.total) }} />
                <div className='bar active-bar' css={{ height: getBarHeight(dataUnit.active) }} />
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      <div className='legend text-medium condensed font-weight-bold'>
        <div className='legend-total'>
          {`${props.totalLabel} ${props.dataName}`}
        </div>
        <div className='legend-active'>
          {`${props.activeLabel} ${props.dataName}`}
        </div>
      </div>
    </div>
  );
};

export default BarChart;
